/**
 * PAA (Persistent App Availability) — reader side.
 *
 * Keeps a restricted-country reader reachable as individual proxy domains get
 * blocked. A signed controller endpoint publishes which neutral proxy domains
 * are currently healthy; this module fetches that list, verifies its Ed25519
 * signature against a baked-in public key, and points the reader's API + media
 * traffic at a healthy domain via `window.__PROXY_HOST__`. When the active
 * domain fails, `rotateProxyHost()` advances to the next cached domain.
 *
 * PAA is INERT unless the published shell sets `window.__PAA_URL__`. Open-tier
 * apps (no proxying) never touch it.
 *
 * Storage keys are opaque (matching the security branch convention):
 *   _pa1 — last-good active proxy host (string origin)
 *   _pa2 — cached verified domain list (JSON string[])
 *
 * Signing contract — MUST match infra/edge/paa-controller/src/worker.ts:
 *   canonical = `${issuedAt}.${ttl}.${domains.join(',')}`
 *   sig       = base64( Ed25519-sign( privateKey, utf8(canonical) ) )
 * We verify that signature with crypto.subtle.verify('Ed25519', ...) using the
 * RAW 32-byte public key below. A failed verify means the response is ignored.
 */

/**
 * Controller base URL, injected by the published shell when proxying is in
 * play. Absent → PAA is inert.
 */
const PAA_CONTROLLER: string | undefined =
  typeof window !== 'undefined' ? (window as any).__PAA_URL__ : undefined;

/**
 * RAW 32-byte Ed25519 public key, base64. Verifies the controller's signature.
 *
 * REPLACE: operator runs `node infra/edge/paa-controller/scripts/gen-keys.mjs`
 * and pastes the printed RAW public key here (and sets the matching private key
 * as the controller's PAA_SIGNING_KEY secret). The value below is a clearly-fake
 * placeholder — all zero bytes — so an unconfigured build cannot accidentally
 * trust a real-looking list.
 */
const PAA_PUBKEY = 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA='; // REPLACE: operator runs gen-keys.mjs

/** Opaque localStorage keys (consistent with the security branch). */
const LS_ACTIVE = '_pa1';
const LS_LIST = '_pa2';

/** Set once we've imported the verify key, so we don't re-import per call. */
let verifyKeyPromise: Promise<CryptoKey | null> | null = null;

interface SignedDomains {
  domains: string[];
  issuedAt: number;
  ttl: number;
  sig: string;
}

/** True when a real (non-placeholder) controller URL is configured. */
export function isPaaActive(): boolean {
  return typeof PAA_CONTROLLER === 'string' && PAA_CONTROLLER.length > 0;
}

/**
 * Boot entry point. Safe to call unconditionally — returns immediately when
 * PAA isn't configured. Never throws; PAA failures must not block the app.
 */
export async function initPaa(): Promise<void> {
  if (!isPaaActive()) return;

  // Let the SW drive rotation too: when a proxied request fails at the SW
  // layer it posts PAA_ROTATE, and we advance to the next healthy domain.
  registerRotateListener();

  // (a) Zero-latency warm start: adopt last-good host from storage immediately
  // so the very first API/media call already targets a known-good domain.
  const lastGood = readActive();
  if (lastGood) setActiveHost(lastGood, { persist: false });

  // (b) Refresh from the controller in the background. A failure here leaves
  // the warm-start host in place.
  try {
    const signed = await fetchSignedDomains();
    if (!signed) return;
    const ok = await verifySignedDomains(signed);
    if (!ok) {
      // Unsigned / forged / mismatched — do NOT trust it.
      return;
    }
    const domains = sanitize(signed.domains);
    if (domains.length === 0) return;

    persistList(domains);

    // Prefer keeping the current active host if it's still in the healthy
    // list (avoids a needless switch); otherwise adopt the first published.
    const current = getActiveProxyHost();
    const next = current && domains.includes(current) ? current : domains[0]!;
    setActiveHost(next, { persist: true });
  } catch {
    /* network/parse error — keep warm-start host, stay silent */
  }
}

/**
 * Advance to the next domain in the cached verified list, wrapping past the
 * current active host. Updates window.__PROXY_HOST__ + persisted last-good.
 * Returns the new host, or null if the list is empty/exhausted.
 *
 * "Exhausted" = we've wrapped all the way back to where we started without a
 * different option, i.e. there's only one (or zero) cached domain. Callers use
 * the null return to stop retrying.
 */
export function rotateProxyHost(): string | null {
  const list = readList();
  if (list.length === 0) return null;

  const current = getActiveProxyHost();
  const idx = current ? list.indexOf(current) : -1;

  // Only one cached option. If it's already active there's nothing to rotate
  // to (exhausted → null); otherwise adopt it.
  if (list.length === 1) {
    if (current === list[0]) return null;
    setActiveHost(list[0]!, { persist: true });
    return list[0]!;
  }

  const nextIdx = (idx + 1) % list.length;
  const next = list[nextIdx]!;
  if (next === current) return null; // wrapped fully — exhausted
  setActiveHost(next, { persist: true });
  return next;
}

/** Current active proxy host (origin), or null when none is set. */
export function getActiveProxyHost(): string | null {
  if (typeof window === 'undefined') return null;
  const h = (window as any).__PROXY_HOST__;
  return typeof h === 'string' && h.length > 0 ? h : null;
}

// ---------------------------------------------------------------------------
// internals
// ---------------------------------------------------------------------------

function setActiveHost(host: string, opts: { persist: boolean }): void {
  if (typeof window === 'undefined') return;
  (window as any).__PROXY_HOST__ = host;
  notifySwProxyHost(host);
  if (opts.persist) {
    try {
      localStorage.setItem(LS_ACTIVE, host);
    } catch {
      /* quota — ignore */
    }
  }
}

/**
 * Keep the service worker's view of the active proxy host in sync so it can
 * recognise + cache proxy traffic and signal PAA_ROTATE on failure. Mirrors the
 * SET_OFFLINE_MODE postMessage pattern in state/offline-mode.ts.
 */
function notifySwProxyHost(host: string): void {
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'SET_PROXY_HOST', host });
  } catch {
    /* SW not ready yet — initPaa re-pushes on the next host change, and the
       controllerchange listener below re-syncs once the SW takes control */
  }
}

let rotateListenerBound = false;

/**
 * Listen for the SW's PAA_ROTATE signal (a proxied request failed at the SW
 * layer) and advance to the next healthy domain. Also re-push the active host
 * whenever a new SW takes control, so a freshly-activated worker learns it.
 */
function registerRotateListener(): void {
  if (rotateListenerBound || typeof navigator === 'undefined' || !navigator.serviceWorker) return;
  rotateListenerBound = true;
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if ((event.data as { type?: string } | null)?.type === 'PAA_ROTATE') {
      rotateProxyHost();
    }
  });
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const h = getActiveProxyHost();
    if (h) notifySwProxyHost(h);
  });
}

function readActive(): string | null {
  try {
    const v = localStorage.getItem(LS_ACTIVE);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

function persistList(domains: string[]): void {
  try {
    localStorage.setItem(LS_LIST, JSON.stringify(domains));
  } catch {
    /* quota — ignore */
  }
}

function readList(): string[] {
  try {
    const raw = localStorage.getItem(LS_LIST);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return sanitize(parsed);
  } catch {
    return [];
  }
}

function sanitize(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of input) {
    if (typeof x !== 'string') continue;
    const trimmed = x.trim().replace(/\/+$/, '');
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

async function fetchSignedDomains(): Promise<SignedDomains | null> {
  if (!PAA_CONTROLLER) return null;
  const base = PAA_CONTROLLER.replace(/\/+$/, '');
  const res = await fetch(`${base}/domains`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return null;
  const data = (await res.json()) as Partial<SignedDomains>;
  if (
    !Array.isArray(data.domains) ||
    typeof data.issuedAt !== 'number' ||
    typeof data.ttl !== 'number' ||
    typeof data.sig !== 'string'
  ) {
    return null;
  }
  return {
    domains: data.domains.map((d) => String(d)),
    issuedAt: data.issuedAt,
    ttl: data.ttl,
    sig: data.sig,
  };
}

/**
 * Verify the Ed25519 signature over the canonical string. Returns false on any
 * problem (bad key, bad sig, mismatch) so the caller treats it as untrusted.
 */
async function verifySignedDomains(signed: SignedDomains): Promise<boolean> {
  try {
    const key = await getVerifyKey();
    if (!key) return false;
    // Canonical string — byte-for-byte identical to the controller's signer.
    const canonical = `${signed.issuedAt}.${signed.ttl}.${signed.domains.join(',')}`;
    const sigBytes = base64ToBytes(signed.sig);
    return await crypto.subtle.verify(
      'Ed25519',
      key,
      sigBytes,
      new TextEncoder().encode(canonical),
    );
  } catch {
    return false;
  }
}

function getVerifyKey(): Promise<CryptoKey | null> {
  if (verifyKeyPromise) return verifyKeyPromise;
  verifyKeyPromise = (async () => {
    try {
      const raw = base64ToBytes(PAA_PUBKEY);
      // RAW 32-byte Ed25519 public key — matches gen-keys.mjs RAW output.
      return await crypto.subtle.importKey('raw', raw, { name: 'Ed25519' }, false, [
        'verify',
      ]);
    } catch {
      return null;
    }
  })();
  return verifyKeyPromise;
}

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const buf = new ArrayBuffer(bin.length);
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}
